import { ApiError } from '@commissionsight/sdk';
import { useState } from 'react';
import { useAuth } from '../api.js';
import {
  type DraftedRate,
  type ProductLine,
  applyRateDraft,
  draftRates,
  rejectRateDraft,
} from '../lib/aiRates.js';
import { useCarriers } from '../lib/useCarriers.js';
import { Button, Card, Field, inputClass } from './ui.js';

const PRODUCT_LINES: { value: ProductLine; label: string }[] = [
  { value: 'major_medical', label: 'Major medical (% of premium or flat per member)' },
  { value: 'medicare', label: 'Medicare (CMS rate bands)' },
  { value: 'ancillary', label: 'Ancillary (premium-based schedule)' },
];

/** One-line human summary of a drafted schedule for the review step. */
function summarize(d: DraftedRate): string {
  const cfg = d.config as Record<string, unknown>;
  if (d.productLine === 'major_medical' && Array.isArray(cfg.rates)) {
    return (cfg.rates as { planCode: string | null; rateType: string; rateValue: number }[])
      .map((r) => {
        const who = r.planCode ?? 'default';
        const val =
          r.rateType === 'percent_of_premium'
            ? `${(r.rateValue * 100).toFixed(2)}% of premium`
            : `$${r.rateValue} per member`;
        return `${who}: ${val}`;
      })
      .join(' · ');
  }
  if (d.productLine === 'medicare' && Array.isArray(cfg.cmsTable)) {
    return `${(cfg.cmsTable as unknown[]).length} CMS rate row(s)`;
  }
  if (d.productLine === 'ancillary' && Array.isArray(cfg.yearRates)) {
    return `${(cfg.yearRates as unknown[]).length} policy-year band(s)`;
  }
  return 'Drafted schedule ready for review.';
}

/**
 * Customer-facing AI rate drafting: paste a comp grid / contract excerpt and the AI
 * drafts a rate schedule for review. Applying it writes live rates (never automatic).
 */
export function RateAiDrafter({ onApplied }: { onApplied?: () => void }) {
  const { token, baseUrl, aiPaused } = useAuth();
  const { carriers } = useCarriers();
  const [carrierId, setCarrierId] = useState('');
  const [productLine, setProductLine] = useState<ProductLine>('major_medical');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<DraftedRate | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const reset = () => {
    setDraft(null);
    setError(null);
  };

  const build = async () => {
    if (!carrierId || !source.trim() || busy) return;
    setBusy(true);
    setError(null);
    setNote(null);
    setDraft(null);
    try {
      setDraft(await draftRates(baseUrl, token, { carrierId, productLine, source: source.trim() }));
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : 'Could not draft rates.');
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!draft || busy) return;
    setBusy(true);
    try {
      await applyRateDraft(baseUrl, token, draft.draftId);
      setNote('Rates applied — they’ll be used the next time you process a statement.');
      setDraft(null);
      setSource('');
      onApplied?.();
    } catch (e) {
      setError(
        e instanceof ApiError || e instanceof Error ? e.message : 'Could not apply the draft.',
      );
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!draft) return;
    try {
      await rejectRateDraft(baseUrl, token, draft.draftId);
    } catch {
      // best-effort — clearing locally is enough for the user
    }
    reset();
  };

  return (
    <Card>
      <div className="space-y-3">
        <div>
          <h2 className="font-semibold">Draft rates with AI</h2>
          <p className="mt-0.5 text-sm text-neutral-500">
            Paste your carrier comp grid or contract rates and the AI drafts a rate schedule for you
            to review — nothing is applied until you approve it.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Carrier">
            <select
              className={inputClass}
              value={carrierId}
              onChange={(e) => {
                setCarrierId(e.target.value);
                reset();
              }}
              disabled={aiPaused}
            >
              <option value="">Select a carrier…</option>
              {carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Product line">
            <select
              className={inputClass}
              value={productLine}
              onChange={(e) => {
                setProductLine(e.target.value as ProductLine);
                reset();
              }}
              disabled={aiPaused}
            >
              {PRODUCT_LINES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Your rates (paste the comp grid or describe them)">
          <textarea
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              reset();
            }}
            disabled={aiPaused}
            rows={5}
            placeholder={'e.g. Plan A: 22% of premium\nPlan B: 18% of premium\nRenewals: 5%'}
            className={`${inputClass} font-mono text-xs`}
          />
        </Field>

        <Button
          onClick={build}
          loading={busy && !draft}
          disabled={!carrierId || !source.trim() || aiPaused}
        >
          {busy && !draft ? 'Drafting…' : 'Draft rates'}
        </Button>

        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        {note && <p className="text-sm text-emerald-700 dark:text-emerald-400">{note}</p>}

        {draft && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm dark:border-emerald-900/60 dark:bg-emerald-950/30">
            <p className="font-medium text-emerald-800 dark:text-emerald-300">
              Proposed rates — review before applying
            </p>
            <p className="mt-1 text-emerald-700 dark:text-emerald-400">{summarize(draft)}</p>
            <button
              type="button"
              onClick={() => setShowRaw((s) => !s)}
              className="mt-2 text-xs font-medium text-emerald-700/80 hover:underline dark:text-emerald-400/80"
            >
              {showRaw ? 'Hide details' : 'View details'}
            </button>
            {showRaw && (
              <pre className="mt-1 max-h-40 overflow-auto rounded-lg bg-white/70 p-2 text-[11px] text-neutral-700 dark:bg-neutral-900/60 dark:text-neutral-300">
                {JSON.stringify(draft.config, null, 2)}
              </pre>
            )}
            <div className="mt-3 flex gap-2">
              <Button onClick={apply} loading={busy}>
                Apply rates
              </Button>
              <Button variant="ghost" onClick={discard} disabled={busy}>
                Discard
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
